import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Permission } from '@salon/shared';
import { api, setSessionExpiredHandler } from './lib/api';
import { useAuth, useCan, type Session } from './stores/auth';
import { Spinner } from './components/ui/misc';
import { AppShell } from './components/layout/AppShell';
import { disconnectSocket } from './lib/socket';

const LoginPage = lazy(() => import('./features/auth/LoginPage'));
const RegisterPage = lazy(() => import('./features/auth/RegisterPage'));
const DashboardPage = lazy(() => import('./features/dashboard/DashboardPage'));
const CalendarPage = lazy(() => import('./features/calendar/CalendarPage'));
const WalkInsPage = lazy(() => import('./features/walkins/WalkInsPage'));
const ClientsPage = lazy(() => import('./features/clients/ClientsPage'));
const ClientDetailPage = lazy(() => import('./features/clients/ClientDetailPage'));
const ServicesPage = lazy(() => import('./features/services/ServicesPage'));
const StaffPage = lazy(() => import('./features/staff/StaffPage'));
const StaffDetailPage = lazy(() => import('./features/staff/StaffDetailPage'));
const PosPage = lazy(() => import('./features/pos/PosPage'));
const InvoicesPage = lazy(() => import('./features/pos/InvoicesPage'));
const InvoiceDetailPage = lazy(() => import('./features/pos/InvoiceDetailPage'));
const SettingsPage = lazy(() => import('./features/settings/SettingsPage'));
const PlatformPage = lazy(() => import('./features/platform/PlatformPage'));
const BookingPage = lazy(() => import('./features/public/BookingPage'));
const ManageBookingPage = lazy(() => import('./features/public/ManageBookingPage'));
const ReceiptPage = lazy(() => import('./features/public/ReceiptPage'));

function FullPageSpinner() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner className="h-7 w-7" />
    </div>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuth((s) => s.status);
  const location = useLocation();
  if (status === 'loading') return <FullPageSpinner />;
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return <>{children}</>;
}

function RequirePermission({ perm, children, fallback = '/app/calendar' }: { perm: Permission | Permission[]; children: ReactNode; fallback?: string }) {
  const can = useCan();
  return can(perm) ? <>{children}</> : <Navigate to={fallback} replace />;
}

function HomeRedirect() {
  const session = useAuth((s) => s.session);
  const can = useCan();
  if (!session) return <FullPageSpinner />;
  if (session.user.role === 'SUPER_ADMIN') return <Navigate to="/app/platform" replace />;
  return <Navigate to={can('report:read') ? '/app/dashboard' : '/app/calendar'} replace />;
}

export function App() {
  const setSession = useAuth((s) => s.setSession);
  const qc = useQueryClient();

  useEffect(() => {
    setSessionExpiredHandler(() => {
      setSession(null);
      disconnectSocket();
      qc.clear();
    });
    api
      .get<Session>('/auth/me')
      .then(setSession)
      .catch(() => setSession(null));
  }, [setSession, qc]);

  return (
    <Suspense fallback={<FullPageSpinner />}>
      <Routes>
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/book/:slug" element={<BookingPage />} />
        <Route path="/book/:slug/manage/:token" element={<ManageBookingPage />} />
        <Route path="/receipt/:token" element={<ReceiptPage />} />

        <Route
          path="/app"
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          <Route index element={<HomeRedirect />} />
          <Route path="dashboard" element={<RequirePermission perm="report:read"><DashboardPage /></RequirePermission>} />
          <Route path="calendar" element={<RequirePermission perm="appointment:read" fallback="/app/platform"><CalendarPage /></RequirePermission>} />
          <Route path="walkins" element={<RequirePermission perm="appointment:read"><WalkInsPage /></RequirePermission>} />
          <Route path="clients" element={<RequirePermission perm="client:read"><ClientsPage /></RequirePermission>} />
          <Route path="clients/:id" element={<RequirePermission perm="client:read"><ClientDetailPage /></RequirePermission>} />
          <Route path="pos" element={<RequirePermission perm="pos:use"><PosPage /></RequirePermission>} />
          <Route path="invoices" element={<RequirePermission perm="invoice:read"><InvoicesPage /></RequirePermission>} />
          <Route path="invoices/:id" element={<RequirePermission perm="invoice:read"><InvoiceDetailPage /></RequirePermission>} />
          <Route path="services" element={<RequirePermission perm="service:read"><ServicesPage /></RequirePermission>} />
          <Route path="staff" element={<RequirePermission perm="staff:read"><StaffPage /></RequirePermission>} />
          <Route path="staff/:id" element={<StaffDetailPage />} />
          <Route path="settings" element={<RequirePermission perm="template:manage"><SettingsPage /></RequirePermission>} />
          <Route path="platform" element={<RequirePermission perm="platform:admin" fallback="/app"><PlatformPage /></RequirePermission>} />
        </Route>

        <Route
          path="*"
          element={
            <div className="flex min-h-screen flex-col items-center justify-center gap-2 p-6 text-center">
              <p className="font-heading text-5xl">404</p>
              <p className="text-muted-foreground">This page does not exist.</p>
              <a className="text-primary underline" href="/">
                Go home
              </a>
            </div>
          }
        />
      </Routes>
    </Suspense>
  );
}
